/**
 * WebKit auto-zooms whenever a focused <input>/<textarea> has a computed
 * font-size below 16 CSS px. This helper enforces the 16 px floor and
 * counter-scales the element back to its original visual size.
 *
 * Repeat-safe, null-safe, composes with existing inline transforms.
 */
export const MIN_INPUT_CSS_PX = 16;

const APPLIED_SCALE_MARKER = 'data-mfmf-scale';

const removePreviousCounterScale = (textarea: HTMLTextAreaElement | HTMLInputElement): void => {
  const previousScale = textarea.getAttribute(APPLIED_SCALE_MARKER);
  if (!previousScale) return;

  const existingTransform = textarea.style.transform?.trim() ?? '';
  const nextTransform = existingTransform.endsWith(previousScale)
    ? existingTransform.slice(0, -previousScale.length).trim()
    : existingTransform;

  textarea.style.transform = nextTransform;
  textarea.removeAttribute(APPLIED_SCALE_MARKER);
};

export const ensureHiddenTextareaMinFont = (
  textarea: HTMLTextAreaElement | HTMLInputElement | null | undefined,
  shouldFillParent = false,
): number | null => {
  if (!textarea || typeof window === 'undefined') return null;

  if (shouldFillParent) {
    // The wrapper owns caller transforms (rotation/translation). Reset the
    // inner input transform on every sync so browser-normalized scale values
    // cannot accumulate across edit sessions.
    textarea.style.transform = '';
    textarea.removeAttribute(APPLIED_SCALE_MARKER);

    const computedPx = parseFloat(window.getComputedStyle(textarea).fontSize);
    if (!Number.isFinite(computedPx) || computedPx >= MIN_INPUT_CSS_PX) {
      textarea.style.width = '100%';
      textarea.style.height = '100%';
      return null;
    }

    const counterScale = computedPx / MIN_INPUT_CSS_PX;
    textarea.style.fontSize = `${MIN_INPUT_CSS_PX}px`;
    textarea.style.width = `${100 / counterScale}%`;
    textarea.style.height = `${100 / counterScale}%`;
    textarea.style.transformOrigin = 'left top';
    textarea.style.transform = `scale(${counterScale})`;
    return counterScale;
  }

  // A caller may reset font-size between style syncs. Remove only the scale
  // this helper previously appended before calculating a fresh counter-scale.
  removePreviousCounterScale(textarea);

  const computed = window.getComputedStyle(textarea);
  const computedPx = parseFloat(computed.fontSize);

  if (!Number.isFinite(computedPx) || computedPx >= MIN_INPUT_CSS_PX) {
    return null;
  }

  const counterScale = computedPx / MIN_INPUT_CSS_PX;
  const counterScaleTransform = `scale(${counterScale})`;
  const existingTransform = textarea.style.transform?.trim() ?? '';
  const nextTransform = [existingTransform, counterScaleTransform].filter(Boolean).join(' ');

  textarea.style.fontSize = `${MIN_INPUT_CSS_PX}px`;
  textarea.style.transform = nextTransform;
  textarea.setAttribute(APPLIED_SCALE_MARKER, counterScaleTransform);
  return counterScale;
};
